import { useEffect, useState } from "react";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
} from "@mui/x-data-grid";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  Button,
  useTheme,
  InputAdornment,
  Popover,
  Typography,
  Badge,
  Divider,
  Fade,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FilterListIcon from "@mui/icons-material/FilterList";
import { tokens } from "../theme";
import { listCategories } from "../services/ProductService";

const CustomToolbar = ({
  categoryFilter,
  setCategoryFilter,
  statusFilter,
  setStatusFilter,
  dateRangeFilter,
  setDateRangeFilter,
  searchQuery,
  setSearchQuery,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [categories, setCategories] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await listCategories();
        setCategories(response.data || []);
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };
    fetchCategories();
  }, []);

  const handleClearFilters = () => {
    setCategoryFilter("");
    setStatusFilter("");
    setDateRangeFilter("");
    setSearchQuery("");
  };

  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const popoverId = open ? "filter-popover" : undefined;

  // Calculate number of active filters to show on the badge
  let activeFilterCount = 0;
  if (categoryFilter) activeFilterCount++;
  if (statusFilter && statusFilter !== "all") activeFilterCount++;
  if (dateRangeFilter && dateRangeFilter !== "all") activeFilterCount++;

  // Unified styling for the Select dropdown menus
  const selectMenuProps = {
    TransitionComponent: Fade,
    transitionDuration: 300,
    PaperProps: {
      sx: {
        backgroundColor:
          theme.palette.mode === "dark"
            ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.8)`
            : "rgba(255, 255, 255, 0.95)",
        backdropFilter: "blur(16px)",
        border: `1px solid ${
          theme.palette.mode === "dark"
            ? "rgba(255, 255, 255, 0.15)"
            : "rgba(0, 0, 0, 0.1)"
        }`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 8px 32px 0 rgba(0, 0, 0, 0.4)"
            : "0 8px 32px 0 rgba(0, 0, 0, 0.15)",
        borderRadius: "12px",
        mt: 1,
        "& .MuiMenuItem-root": {
          padding: "12px 20px",
          margin: "4px 8px",
          gap: "12px",
          borderRadius: "8px",
          transition: "all 0.2s ease-in-out",
          fontWeight: 600,
          fontSize: "14px",
          "&:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.1) !important"
                : "rgba(0, 0, 0, 0.04) !important",
          },
          "&:active": { transform: "scale(0.95)" },
          "&.Mui-selected": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.15)"
                : "rgba(0, 0, 0, 0.08)",
            fontWeight: "bold",
          },
          "&.Mui-selected:hover": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.2)"
                : "rgba(0, 0, 0, 0.12)",
          },
        },
      },
    },
  };

  return (
    <GridToolbarContainer
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "16px 24px",
        gap: "16px",
      }}
    >
      {/* Left side: Standard DataGrid Actions mixed with Custom Filter */}
      <Box
        display="flex"
        alignItems="center"
        flexWrap={{ xs: "nowrap", sm: "wrap" }}
        gap={{ xs: 0, sm: 1 }}
        sx={{
          width: { xs: "100%", sm: "100%", md: "auto" },
          justifyContent: {
            xs: "space-between",
            sm: "space-between",
            md: "flex-start",
          },
        }}
      >
        <GridToolbarColumnsButton />

        {/* Custom Filters Button designed like default actions */}
        <Badge
          badgeContent={activeFilterCount}
          sx={{
            "& .MuiBadge-badge": {
              backgroundColor: colors.blueAccent[500],
              color: "#ffffff",
              fontWeight: "bold",
            },
          }}
        >
          <Button
            size="small"
            variant="text"
            onClick={handleFilterClick}
            startIcon={<FilterListIcon />}
            sx={{
              textTransform: "uppercase",
              fontWeight: 500,
            }}
          >
            Filters
          </Button>
        </Badge>

        <GridToolbarDensitySelector />
        <GridToolbarExport />
      </Box>

      {/* Right side: Search Bar */}
      <Box
        display="flex"
        flexGrow={1}
        justifyContent="flex-end"
        minWidth={{ xs: "100%", sm: "250px" }}
      >
        {/* The Consolidated Filter Popover */}
        <Popover
          id={popoverId}
          open={open}
          anchorEl={anchorEl}
          onClose={handleFilterClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "left",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "left",
          }}
          disableScrollLock
          PaperProps={{
            sx: {
              mt: 1,
              p: 2.5,
              width: 280,
              backgroundColor:
                theme.palette.mode === "dark"
                  ? `rgba(${parseInt(colors.primary[400].slice(1, 3), 16)}, ${parseInt(colors.primary[400].slice(3, 5), 16)}, ${parseInt(colors.primary[400].slice(5, 7), 16)}, 0.9)`
                  : "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(16px)",
              border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.15)" : "rgba(0, 0, 0, 0.1)"}`,
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 12px 40px 0 rgba(0, 0, 0, 0.5)"
                  : "0 12px 40px 0 rgba(0, 0, 0, 0.15)",
              borderRadius: "12px",
              display: "flex",
              flexDirection: "column",
              gap: 2,
            },
          }}
        >
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            color={colors.gray[100]}
          >
            Filter Options
          </Typography>
          <Divider
            sx={{
              borderColor:
                theme.palette.mode === "dark"
                  ? "rgba(255,255,255,0.1)"
                  : "rgba(0,0,0,0.1)",
            }}
          />

          {/* Category Filter */}
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography
              variant="caption"
              color={colors.gray[400]}
              fontWeight={600}
              textTransform="uppercase"
            >
              Category
            </Typography>
            <Select
              displayEmpty
              size="small"
              value={categoryFilter}
              renderValue={() =>
                categoryFilter ? categoryFilter : "All Categories"
              }
              onChange={(e) => setCategoryFilter(e.target.value)}
              MenuProps={selectMenuProps}
              sx={{
                height: "38px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "#ffffff",
                color: colors.gray[100],
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.15)"
                      : "rgba(0,0,0,0.15)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.blueAccent[400],
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.blueAccent[500],
                },
              }}
            >
              <MenuItem value="" sx={{ color: colors.gray[100] }}>
                All Categories
              </MenuItem>
              {categories.length > 0 ? (
                categories.map((cat, idx) => (
                  <MenuItem
                    key={idx}
                    value={cat}
                    sx={{ color: colors.gray[100] }}
                  >
                    {cat}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled sx={{ color: colors.gray[300] }}>
                  No Categories Found
                </MenuItem>
              )}
            </Select>
          </Box>

          {/* Status Filter */}
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography
              variant="caption"
              color={colors.gray[400]}
              fontWeight={600}
              textTransform="uppercase"
            >
              Status
            </Typography>
            <Select
              displayEmpty
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              MenuProps={selectMenuProps}
              sx={{
                height: "38px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "#ffffff",
                color: colors.gray[100],
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.15)"
                      : "rgba(0,0,0,0.15)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.blueAccent[400],
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.blueAccent[500],
                },
              }}
            >
              <MenuItem value="" disabled sx={{ color: colors.gray[300] }}>
                Select Status
              </MenuItem>
              <MenuItem value="all" sx={{ color: colors.gray[100] }}>
                All Statuses
              </MenuItem>
              <MenuItem value="healthy" sx={{ color: colors.greenAccent[300] }}>
                Healthy
              </MenuItem>
              <MenuItem value="low" sx={{ color: colors.yellowAccent[500] }}>
                Low Stock
              </MenuItem>
              <MenuItem value="out" sx={{ color: colors.redAccent[300] }}>
                Out of Stock
              </MenuItem>
            </Select>
          </Box>

          {/* Date Filter */}
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography
              variant="caption"
              color={colors.gray[400]}
              fontWeight={600}
              textTransform="uppercase"
            >
              Date Updated
            </Typography>
            <Select
              displayEmpty
              size="small"
              value={dateRangeFilter}
              onChange={(e) => setDateRangeFilter(e.target.value)}
              MenuProps={selectMenuProps}
              sx={{
                height: "38px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.05)"
                    : "#ffffff",
                color: colors.gray[100],
                ".MuiOutlinedInput-notchedOutline": {
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(255, 255, 255, 0.15)"
                      : "rgba(0,0,0,0.15)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.blueAccent[400],
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: colors.blueAccent[500],
                },
              }}
            >
              <MenuItem value="" disabled sx={{ color: colors.gray[300] }}>
                Select Timeframe
              </MenuItem>
              <MenuItem value="all" sx={{ color: colors.gray[100] }}>
                All Time
              </MenuItem>
              <MenuItem value="24h" sx={{ color: colors.gray[100] }}>
                Last 24 Hours
              </MenuItem>
              <MenuItem value="7d" sx={{ color: colors.gray[100] }}>
                Last 7 Days
              </MenuItem>
              <MenuItem value="30d" sx={{ color: colors.gray[100] }}>
                Last 30 Days
              </MenuItem>
            </Select>
          </Box>

          {/* Clear Button */}
          <Button
            size="small"
            variant="outlined"
            onClick={handleClearFilters}
            sx={{
              mt: 1,
              borderColor: colors.redAccent[500],
              color: colors.redAccent[500],
              textTransform: "none",
              fontWeight: 600,
              "&:hover": {
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(244, 63, 94, 0.1)"
                    : "rgba(225, 29, 72, 0.05)",
                borderColor: colors.redAccent[400],
              },
            }}
          >
            Clear All Filters
          </Button>
        </Popover>

        {/* Search Bar */}
        <TextField
          variant="outlined"
          size="small"
          placeholder="Search by name or SKU"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: colors.gray[100] }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: "100%",
            maxWidth: { md: "300px" },
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(255, 255, 255, 0.05)"
                : colors.primary[400],
            borderRadius: "4px",
            transition: "all 0.2s ease",
            "& .MuiOutlinedInput-root": {
              height: "38px",
              color: colors.gray[100],
              "& fieldset": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.15)"
                    : "rgba(0,0,0,0.15)",
              },
              "&:hover fieldset": {
                borderColor:
                  theme.palette.mode === "dark"
                    ? "rgba(255, 255, 255, 0.3)"
                    : colors.gray[500],
              },
              "&.Mui-focused fieldset": {
                borderColor: colors.blueAccent[500],
              },
            },
          }}
        />
      </Box>
    </GridToolbarContainer>
  );
};

export default CustomToolbar;
